import { Loader2, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApplicationNotes } from '@/hooks/useApplicationNotes';
import { formatDateTime } from '@/lib/dateUtils';
import { cn } from '@/lib/utils';
import type { Application, RecruiterNote } from '@/types';

type Props = {
  app: Application;
  companyId: string | undefined;
  userId: string | undefined;
  t: (key: string, params?: Record<string, string | number>) => string;
  writeBlocked?: boolean;
};

function NoteMeta({
  note,
  t,
  visibilityLabel,
}: {
  note: RecruiterNote;
  t: Props['t'];
  visibilityLabel: string;
}) {
  const showUpdated = note.updatedAt !== note.createdAt;

  return (
    <div className="min-w-0 flex-1 space-y-1">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <p className="text-sm font-medium text-foreground">
          {note.authorDisplayName ?? t('appDetail.notesUnknownAuthor')}
        </p>
        <span className="text-xs text-muted-foreground">{visibilityLabel}</span>
      </div>
      <div className="space-y-0.5 text-xs text-muted-foreground">
        <p>{t('appDetail.notesCreatedAt', { date: formatDateTime(note.createdAt) })}</p>
        {showUpdated ? (
          <p>{t('appDetail.notesUpdatedAt', { date: formatDateTime(note.updatedAt) })}</p>
        ) : null}
      </div>
    </div>
  );
}

function ReadOnlyNoteCard({
  note,
  t,
  visibilityLabel,
}: {
  note: RecruiterNote;
  t: Props['t'];
  visibilityLabel: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
      <NoteMeta note={note} t={t} visibilityLabel={visibilityLabel} />
      <p className="text-sm whitespace-pre-wrap">{note.body}</p>
    </div>
  );
}

function SavedNoteCard({
  note,
  t,
  visibilityLabel,
  writeBlocked,
  onEdit,
}: {
  note: RecruiterNote;
  t: Props['t'];
  visibilityLabel: string;
  writeBlocked?: boolean;
  onEdit: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <NoteMeta note={note} t={t} visibilityLabel={visibilityLabel} />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 shrink-0 gap-1.5 px-2"
          disabled={writeBlocked}
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" />
          {t('appDetail.notesEdit')}
        </Button>
      </div>
      <p className="text-sm whitespace-pre-wrap">{note.body}</p>
    </div>
  );
}

function NoteEditor({
  id,
  label,
  hint,
  value,
  onChange,
  onSave,
  onCancel,
  saving,
  writeBlocked,
  showCancel,
  saveLabel,
  cancelLabel,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel?: () => void;
  saving: boolean;
  writeBlocked?: boolean;
  showCancel?: boolean;
  saveLabel: string;
  cancelLabel: string;
}) {
  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor={id}>{label}</Label>
        {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
      </div>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        disabled={writeBlocked || saving}
        className="resize-y min-h-[120px]"
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={onSave}
          disabled={writeBlocked || saving || !value.trim()}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {saveLabel}
        </Button>
        {showCancel && onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
            {cancelLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function ApplicationNotesTab({
  app,
  companyId,
  userId,
  t,
  writeBlocked = false,
}: Props) {
  const {
    ownPrivateNote,
    ownTeamNote,
    otherTeamNotes,
    privateDraft,
    setPrivateDraft,
    teamDraft,
    setTeamDraft,
    editingPrivate,
    editingTeam,
    startEditPrivate,
    startEditTeam,
    cancelEditPrivate,
    cancelEditTeam,
    loading,
    savingPrivate,
    savingTeam,
    savePrivateNote,
    saveTeamNote,
  } = useApplicationNotes(companyId, app, userId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const showPrivateEditor = editingPrivate || !ownPrivateNote;
  const showTeamEditor = editingTeam || !ownTeamNote;

  return (
    <div className="w-full max-w-3xl space-y-8">
      <section className="space-y-3 min-w-0">
        <h3 className="text-sm font-medium">{t('appDetail.notesPrivateSection')}</h3>
        {showPrivateEditor ? (
          <NoteEditor
            id="recruiter-note-private"
            label={t('appDetail.notesYourPrivateNote')}
            hint={t('appDetail.notesVisibilityPrivateHint')}
            value={privateDraft}
            onChange={setPrivateDraft}
            onSave={() => void savePrivateNote()}
            onCancel={ownPrivateNote ? cancelEditPrivate : undefined}
            showCancel={!!ownPrivateNote}
            saving={savingPrivate}
            writeBlocked={writeBlocked}
            saveLabel={t('appDetail.notesSave')}
            cancelLabel={t('appDetail.notesCancel')}
          />
        ) : ownPrivateNote ? (
          <SavedNoteCard
            note={ownPrivateNote}
            t={t}
            visibilityLabel={t('appDetail.notesVisibilityPrivate')}
            writeBlocked={writeBlocked}
            onEdit={startEditPrivate}
          />
        ) : null}
      </section>

      <section className="space-y-3 min-w-0">
        <h3 className="text-sm font-medium">{t('appDetail.notesTeamSection')}</h3>
        {showTeamEditor ? (
          <NoteEditor
            id="recruiter-note-team"
            label={t('appDetail.notesYourTeamNote')}
            hint={t('appDetail.notesVisibilityPublicHint')}
            value={teamDraft}
            onChange={setTeamDraft}
            onSave={() => void saveTeamNote()}
            onCancel={ownTeamNote ? cancelEditTeam : undefined}
            showCancel={!!ownTeamNote}
            saving={savingTeam}
            writeBlocked={writeBlocked}
            saveLabel={t('appDetail.notesSave')}
            cancelLabel={t('appDetail.notesCancel')}
          />
        ) : ownTeamNote ? (
          <SavedNoteCard
            note={ownTeamNote}
            t={t}
            visibilityLabel={t('appDetail.notesVisibilityPublic')}
            writeBlocked={writeBlocked}
            onEdit={startEditTeam}
          />
        ) : null}

        {otherTeamNotes.length > 0 ? (
          <ul className={cn('space-y-3', ownTeamNote || showTeamEditor ? 'pt-1' : undefined)}>
            {otherTeamNotes.map((note) => (
              <li key={note.id}>
                <ReadOnlyNoteCard
                  note={note}
                  t={t}
                  visibilityLabel={t('appDetail.notesVisibilityPublic')}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
